/**
 * 「忙完回家」的三个口（UF-INTENT F0 第 2 节；后端 C 的 plan_requests 命令 + I 的路由，CR-1 修订）：
 * - POST /pets/{pet_id}/plan-requests：建请求（CSRF＋Idempotency-Key）。只有两条路——按钮（origin=button），或主人自己那条私聊下的
 *   确认条（origin=text_confirmed，带那条消息号）。带主人确认时看到的旅程号与行程版本，服务端对不上就不执行（409 VERSION_CONFLICT）。
 * - POST …/{request_id}/cancel：撤回（原请求人或管理员；带看到的请求版本）。
 * - GET …/current：纯读。请求人拿自己那条的显示状态；其他家人只拿中性安排，拿不到是谁请的。
 *
 * 为什么放在通讯器模块里、不进 shared/services：同 ./petFriends.service（shared 这一层要报主窗口批了才动）。
 * 演示世界没有真的行程可改：演示读口报「还没开放」，页面不显示按钮，不编状态。
 */
import type { PlanRequestCurrent, PlanRequestOrigin, PlanRequestResult, PlanRequestView } from "@/shared/contracts";
import type { ApiClient } from "@/shared/api/client";
import { ApiError } from "@/shared/api/errors";

export interface ComeHomeAsk {
  journeyId: string;
  itineraryVersion: number;
  origin: PlanRequestOrigin;
  /** text_confirmed：确认条所在的那条主人消息；按钮为空 */
  sourceMessageId: string | null;
}

export interface ComeHomeService {
  current(petId: string, signal?: AbortSignal): Promise<PlanRequestCurrent>;
  ask(petId: string, ask: ComeHomeAsk, idempotencyKey: string): Promise<PlanRequestResult>;
  cancel(petId: string, requestId: string, expectedVersion: number, idempotencyKey: string): Promise<PlanRequestView>;
}

const base = (petId: string) => `/pets/${encodeURIComponent(petId)}/plan-requests`;

export function liveComeHomeService(api: ApiClient): ComeHomeService {
  return {
    current: (petId, signal) => api.request<PlanRequestCurrent>(`${base(petId)}/current`, { signal }),
    ask: (petId, ask, idempotencyKey) =>
      api.request<PlanRequestResult>(base(petId), {
        method: "POST",
        idempotencyKey,
        body: { journey_id: ask.journeyId, expected_itinerary_version: ask.itineraryVersion, origin: ask.origin, source_message_id: ask.sourceMessageId },
      }),
    cancel: (petId, requestId, expectedVersion, idempotencyKey) =>
      api.request<PlanRequestView>(`${base(petId)}/${encodeURIComponent(requestId)}/cancel`, {
        method: "POST",
        idempotencyKey,
        body: { expected_version: expectedVersion },
      }),
  };
}

export function demoComeHomeService(): ComeHomeService {
  const closed = async (): Promise<never> => {
    throw ApiError.capability("pets.plan_requests", "演示模式里不能改行程。");
  };
  return { current: closed, ask: closed, cancel: closed };
}
